import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  HStack,
  Stack,
  Text,
  Textarea,
  useDisclosure,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  type BuyerParticipationRequestDetailDto,
  type BuyerBidEspdDto,
  useGetParticipationRequestRejectionDocumentsQuery,
  useGetSourcingEventQuery,
  useSetQualificationVerdictMutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useExclusiveAction } from "../../../../common/hooks/useExclusiveAction";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { RejectionDetails } from "../../../../common/rejection/RejectionDetails";
import {
  type RejectionSubmission,
  RejectSupplierModal,
} from "../../../../common/rejection/RejectSupplierModal";
import { getQualificationRequirementGroundKey } from "../../../../common/rejection/rejectionGrounds";
import { QualificationVerdictControl } from "../../bid-detail/QualificationVerdictControl";
import { formatDecidedBy } from "./formatDecidedBy";
import { getRequestQualificationProgress } from "./requestQualification";
import { useBuyerReviewNotesEditor } from "./useBuyerReviewNotesEditor";
import { SaveStatusBadge } from "./SaveStatusBadge";

interface Props {
  eventId: string;
  detail: BuyerParticipationRequestDetailDto;
  optimisticReviewed?: number;
  onShowEspd?: () => void;
}

// Counts criteria that have answer !== OK (deviations + unanswered).
const deriveEspdDigest = (espd: BuyerBidEspdDto): { flagged: number; total: number } => {
  const flagged = (espd.deviationCount ?? 0) + (espd.unansweredCount ?? 0);
  const total = (espd.okCount ?? 0) + flagged;
  return { flagged, total };
};

export const QualificationDecisionCard = ({ eventId, detail, optimisticReviewed, onShowEspd }: Props) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const apiError = useApiError();
  const [setVerdict] = useSetQualificationVerdictMutation();
  const rejectionModal = useDisclosure();

  const request = detail.request;
  const verdict = request.qualificationVerdict ?? null;
  const locked = detail.invitationConfirmed ?? false;
  const clearable = detail.verdictClearable ?? true;
  const isRejected = verdict?.qualified === false;
  // The BFF always returns an ESPD DTO; hasResponse=false means no ESPD was submitted.
  const espdDigest = detail.espd?.hasResponse ? deriveEspdDigest(detail.espd) : null;

  const editor = useBuyerReviewNotesEditor(eventId, request.id, {
    internalNote: request.internalNote,
    rejectionReason: request.rejectionReason,
  });

  const progress = getRequestQualificationProgress(
    detail.selectionCriteria ?? [],
    request.qualificationResponses ?? [],
  );

  // The "all reviewed" gate respects an optimistic count so the parent can unblock
  // the Qualified button before the refetch settles.
  const effectiveReviewed = optimisticReviewed ?? progress.reviewed;
  const allReviewed = !progress.hasRequirements || effectiveReviewed >= progress.total;
  const pendingCount = progress.hasRequirements ? progress.total - effectiveReviewed : 0;

  const [runVerdictAction, isSettingVerdict] = useExclusiveAction((action: () => Promise<void>) => action());

  const submitVerdict = (qualified: boolean | null) =>
    runVerdictAction(async () => {
      try {
        await editor.flush();
        await setVerdict({
          eventId,
          requestId: request.id,
          setQualificationVerdictRequestDto: { qualified },
        }).unwrap();
      } catch (e) {
        apiError.trigger(e);
      }
    });

  const handleVerdictChange = async (value: boolean | null) => {
    // Disqualifying is never a bare toggle — it goes through the structured § 24-2 / § 9-5 flow.
    if (value === false) {
      rejectionModal.onOpen();
      return;
    }
    await submitVerdict(value);
  };

  const submitRejection = (submission: RejectionSubmission) =>
    runVerdictAction(async () => {
      try {
        await editor.flush();
        await setVerdict({
          eventId,
          requestId: request.id,
          setQualificationVerdictRequestDto: {
            qualified: false,
            rejectionReason: submission.reason,
            rejection: {
              grounds: submission.grounds,
              message: submission.message,
              attachmentIds: submission.attachmentIds,
            },
          },
        }).unwrap();
        rejectionModal.onClose();
      } catch (e) {
        apiError.trigger(e);
      }
    });

  // Locked view: read-only rendering with no buttons or textareas.
  if (locked) {
    return (
      <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
        <CardBody>
          <Stack spacing="4">
            <Box>
              <Text fontWeight="semibold" color="smText" mb="1">
                {t("Qualification decision")}
              </Text>
              <Text fontSize="sm" color="smMuted">
                {t("The invitation is confirmed — the decision is final.")}
              </Text>
            </Box>

            {espdDigest && <EspdDigestText digest={espdDigest} />}

            {request.internalNote && (
              <Box>
                <Text fontSize="sm" fontWeight="medium" color="smText" mb="1">
                  {t("Internal note")}
                </Text>
                <Text fontSize="sm" color="smText">
                  {request.internalNote}
                </Text>
              </Box>
            )}

            {verdict && (
              <Box>
                <HStack mb="1">
                  <Badge colorScheme={verdict.qualified ? "green" : "red"}>
                    {verdict.qualified ? t("Qualified") : t("Not qualified")}
                  </Badge>
                </HStack>
                {isRejected && (
                  <RejectionDetails
                    grounds={request.rejection?.grounds}
                    reason={request.rejectionReason}
                    message={request.rejection?.message}
                    attachments={request.rejection?.attachments}
                    attachmentIds={request.rejection?.attachmentIds}
                  />
                )}
                <Text fontSize="sm" color="smMuted" mt="2">
                  {formatDecidedBy(verdict, displayDateWithTime, t)}
                </Text>
              </Box>
            )}
          </Stack>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
      <CardBody>
        <Stack spacing="5">
          <Text fontWeight="semibold" color="smText">
            {t("Qualification decision")}
          </Text>

          {/* ESPD digest — informational only, never gates the verdict */}
          {espdDigest &&
            (espdDigest.flagged > 0 ? (
              <Alert status="info" rounded="md" fontSize="sm" py="2">
                <AlertIcon />
                <Button
                  variant="link"
                  fontSize="sm"
                  color="inherit"
                  onClick={onShowEspd}
                  fontWeight="normal"
                >
                  {t("ESPD: {{flagged}} of {{total}} declarations need attention — see the ESPD tab", {
                    flagged: espdDigest.flagged,
                    total: espdDigest.total,
                  })}
                </Button>
              </Alert>
            ) : (
              <EspdDigestText digest={espdDigest} />
            ))}

          {/* Internal note */}
          <Box>
            <HStack mb="1" align="center">
              <Text fontSize="sm" fontWeight="medium" color="smText">
                {t("Internal note")}
              </Text>
              <SaveStatusBadge status={editor.statusFor("internalNote")} />
            </HStack>
            <Textarea
              aria-label={t("Internal note") ?? undefined}
              value={editor.values.internalNote}
              onChange={(e) => editor.setField("internalNote", e.target.value)}
              bg="smBackgroundSecondary"
              rounded="md"
              fontSize="sm"
              mb="1"
            />
            <Text fontSize="xs" color="smMuted">
              {t("Only visible to the buying team. Never shared with the supplier.")}
            </Text>
          </Box>

          {/* Verdict toggle */}
          <Box>
            <QualificationVerdictControl
              value={verdict?.qualified ?? null}
              onChange={handleVerdictChange}
              isReadOnly={isSettingVerdict}
              isPositiveDisabled={!allReviewed && verdict?.qualified !== true}
              isClearable={clearable}
              positiveLabel={t("Qualified")}
              negativeLabel={t("Not qualified")}
            />

            {!allReviewed && verdict?.qualified !== true && (
              <Text fontSize="xs" color="smMuted" mt="1">
                {t("Review all qualification requirements first ({{count}} remaining)", {
                  count: pendingCount,
                })}
              </Text>
            )}

            {verdict && (
              <Box mt="2" fontSize="sm" color="smMuted">
                <Text>
                  {formatDecidedBy(verdict, displayDateWithTime, t)}
                </Text>
                <Text fontSize="xs">
                  {clearable
                    ? t("Click the active choice to undo the decision")
                    : t("The decision has been sent to the supplier. To change it, choose the other decision.")}
                </Text>
              </Box>
            )}
          </Box>

          {/* Registered rejection — read-only; undo it via the verdict toggle above */}
          {isRejected && (
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="smText" mb="2">
                {t("Avvisning")}
              </Text>
              <RejectionDetails
                grounds={request.rejection?.grounds}
                reason={request.rejectionReason}
                message={request.rejection?.message}
                attachments={request.rejection?.attachments}
                attachmentIds={request.rejection?.attachmentIds}
              />
              <Text fontSize="xs" color="smMuted" mt="2">
                {clearable
                  ? t("Sent to the supplier with the rejection when the invitation is confirmed.")
                  : t("Sent to the supplier with the rejection when it was decided.")}
              </Text>
            </Box>
          )}

          {/* Retracted-review warning — only for qualified verdicts; for rejections we show
              the "(N remaining)" helper above which is sufficient */}
          {verdict?.qualified === true && progress.hasRequirements && pendingCount > 0 && (
            <Alert status="warning" rounded="md" fontSize="sm" py="2">
              <AlertIcon />
              {t("The review changed after the decision — confirm it again.")}
            </Alert>
          )}
        </Stack>
      </CardBody>

      {rejectionModal.isOpen && (
        <RejectSupplierRequestModal
          eventId={eventId}
          requestId={request.id}
          supplierName={request.organizationName ?? ""}
          organizationNumber={request.organizationNumber}
          foaPart={detail.foaPart}
          isOpen={rejectionModal.isOpen}
          onClose={rejectionModal.onClose}
          onSubmit={submitRejection}
          isSubmitting={isSettingVerdict}
        />
      )}
    </Card>
  );
};

const EspdDigestText = ({ digest }: { digest: { flagged: number; total: number } }) => {
  const { t } = useTranslation();

  return (
    <Text fontSize="sm" color="smMuted">
      {digest.flagged > 0
        ? t("ESPD: {{flagged}} of {{total}} declarations need attention — see the ESPD tab", {
            flagged: digest.flagged,
            total: digest.total,
          })
        : t("ESPD: no declarations need attention")}
    </Text>
  );
};

const RejectSupplierRequestModal = ({
  eventId,
  requestId,
  supplierName,
  organizationNumber,
  foaPart,
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  eventId: string;
  requestId: string;
  supplierName: string;
  organizationNumber?: string | null;
  foaPart: BuyerParticipationRequestDetailDto["foaPart"];
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (submission: RejectionSubmission) => Promise<void>;
  isSubmitting: boolean;
}) => {
  const { data, refetch } = useGetParticipationRequestRejectionDocumentsQuery({
    eventId,
    requestId,
  });
  const { data: event } = useGetSourcingEventQuery({ eventId });
  const qualificationGroundKey = getQualificationRequirementGroundKey(foaPart);

  return (
    <RejectSupplierModal
      isOpen={isOpen}
      onClose={onClose}
      supplierName={supplierName}
      organizationNumber={organizationNumber}
      foaPart={foaPart}
      initialGroundKeys={qualificationGroundKey ? [qualificationGroundKey] : []}
      outsideProcurementAct={event?.outsideProcurementAct ?? false}
      attachments={{
        entityType: "ParticipationRequestRejection",
        entityId: requestId,
        eventId,
        documents: data?.documents ?? [],
        refetch,
      }}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
    />
  );
};
